import { canvasAabbOfRotatedPathBounds } from '@/lib/boundaries/bounding-box-boundary'
import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import type { ShapeData } from '@/lib/shapes/types/shape-data'

/** Axis-aligned canvas bbox of the path after rotation (matches `ObjectPanel` / `ShapeRenderer`). */
export function canvasAabbForLayoutShape(
  shape: ShapeData,
  pathBounds: ShapeBounds | undefined
): { minX: number; minY: number; maxX: number; maxY: number } {
  // Pen paths (type === 'path') may not fill their placement rect — the SVG path
  // occupies only the region reported by getBBox(), which can have a non-zero x/y
  // offset. canvasAabbOfRotatedPathBounds ignores pathBounds for axis-aligned shapes
  // (to prevent stroke inflation for standard shapes), but that optimisation is wrong
  // for pen paths where the path genuinely does not fill the placement rect.
  if (shape.type === 'path' && pathBounds != null) {
    const rot = shape.rotation ?? 0
    const rNorm = ((rot % 360) + 360) % 360
    const isAxisAligned = rNorm <= 1e-4 || rNorm >= 360 - 1e-4

    if (isAxisAligned) {
      return {
        minX: shape.x + pathBounds.x,
        minY: shape.y + pathBounds.y,
        maxX: shape.x + pathBounds.x + pathBounds.width,
        maxY: shape.y + pathBounds.y + pathBounds.height,
      }
    }
  }

  return canvasAabbOfRotatedPathBounds({
    shapeX: shape.x,
    shapeY: shape.y,
    shapeWidth: shape.width,
    shapeHeight: shape.height,
    boundsX: pathBounds?.x ?? 0,
    boundsY: pathBounds?.y ?? 0,
    boundsWidth: pathBounds?.width ?? shape.width,
    boundsHeight: pathBounds?.height ?? shape.height,
    rotationDeg: shape.rotation ?? 0,
  })
}
